import React, { useEffect, useState } from 'react';
import { X, Mail, Lock, ChevronRight, Zap, Youtube, Github, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  latestDay: number;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, latestDay }) => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  // Handle animation sequencing for entering
  useEffect(() => {
    if (isOpen) {
      // Small delay to allow container to render before fading in content
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
        document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAdminClick = () => {
    onClose();
    navigate('/admin');
  };

  // Calculate progress percentage (clamped to 100%)
  const progressPercent = Math.min((latestDay / 365) * 100, 100).toFixed(1);

  if (!isOpen && !showContent) return null;

  return (
    <div 
        className={`fixed inset-0 z-[100] transition-all duration-500 ease-in-out ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
    >
      {/* Massive Glass Background */}
      <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-2xl md:backdrop-blur-3xl transition-all duration-500" />
      
      {/* Content Container - Full Screen Flex */}
      <div className="relative z-10 w-full h-full flex flex-col md:flex-row overflow-hidden">
        
        {/* CLOSE BUTTON - Absolute Top Right */}
         <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-3 rounded-full bg-black/5 dark:bg-white/10 text-slate-800 dark:text-white hover:bg-black/10 dark:hover:bg-white/20 transition-all hover:rotate-90 z-50 group"
          >
            <X size={24} />
         </button>

         {/* Left/Top Section: Brand & Mission */}
         <div className={`flex-1 flex flex-col justify-center p-8 md:p-16 lg:p-24 transition-all duration-700 delay-100 transform ${showContent ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-6xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tighter mb-6 italic drop-shadow-sm">
              IQODEX
            </h2>
            <div className="inline-flex mb-8">
                <span className="px-3 py-1 rounded-full bg-primary/10 dark:bg-primary/20 text-primary text-xs md:text-sm font-bold uppercase tracking-widest border border-primary/20">
                    2026 Vision
                </span>
            </div>
             <p className="text-lg md:text-2xl text-slate-600 dark:text-slate-300 font-light max-w-xl leading-relaxed">
              The journey of building <strong className="text-slate-900 dark:text-white font-semibold">365 web applications</strong> in one year. 
              <br className="hidden md:block"/>Pushing the limits of code, creativity, and consistency.
            </p>

            {/* Streak Status */}
            <div className="mt-12 p-6 rounded-2xl bg-white/40 dark:bg-white/5 border border-white/20 dark:border-white/10 backdrop-blur-md max-w-md group hover:border-primary/30 transition-colors shadow-xl">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <Zap size={18} className="text-yellow-500 fill-current" />
                        <span className="text-sm font-bold text-slate-800 dark:text-white tracking-wide uppercase">Current Streak</span>
                    </div>
                    <span className="text-sm font-mono text-primary font-bold">Day {latestDay}/365</span>
                </div>
                <div className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-primary to-accent shadow-[0_0_15px_rgba(99,102,241,0.6)] animate-pulse transition-all duration-1000"
                        style={{ width: `${progressPercent}%` }}
                    ></div>
                </div>
            </div>
         </div>

         {/* Right/Bottom Section: Connect & Links */}
         <div className={`flex-1 flex flex-col justify-center p-8 md:p-16 bg-white/30 dark:bg-black/20 border-t md:border-t-0 md:border-l border-white/10 transition-all duration-700 delay-200 transform ${showContent ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto md:mx-0">
               {/* TikTok */}
               <a href="https://www.tiktok.com/@my365journeycode" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 rounded-2xl bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 border border-white/20 dark:border-white/5 transition-all group hover:scale-[1.02] shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center group-hover:bg-pink-500/10 transition-colors">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-slate-700 dark:text-slate-300 group-hover:text-pink-500">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">TikTok</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">@my365journeycode</p>
                  </div>
                  <ExternalLink size={16} className="ml-auto text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white" />
               </a>

               {/* YouTube */}
               <a href="https://www.youtube.com/@365Jurney" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 rounded-2xl bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 border border-white/20 dark:border-white/5 transition-all group hover:scale-[1.02] shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center group-hover:bg-red-500/10 transition-colors">
                    <Youtube size={20} className="text-slate-700 dark:text-slate-300 group-hover:text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">YouTube</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">@365Jurney</p>
                  </div>
                  <ExternalLink size={16} className="ml-auto text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white" />
               </a>

               {/* GitHub */}
               <a href="https://github.com/2026coder" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 rounded-2xl bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 border border-white/20 dark:border-white/5 transition-all group hover:scale-[1.02] shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center group-hover:bg-slate-500/10 transition-colors">
                    <Github size={20} className="text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">GitHub</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">@2026coder</p>
                  </div>
                  <ExternalLink size={16} className="ml-auto text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white" />
               </a>

               {/* Email / Collab */}
               <a href="mailto:365jurneyhelp@gmail.com" className="flex items-center gap-4 p-5 rounded-2xl bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 border border-white/20 dark:border-white/5 transition-all group hover:scale-[1.02] shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                    <Mail size={20} className="text-slate-700 dark:text-slate-300 group-hover:text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">Contact</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Collabs & Inquiries</p>
                  </div>
                  <ChevronRight size={16} className="ml-auto text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white" />
               </a>
            </div>

            {/* Admin Footer */}
            <div className="mt-12 flex items-center gap-2 text-slate-500 dark:text-slate-500 text-xs">
                <button 
                  onClick={handleAdminClick} 
                  className="p-2 bg-black/5 dark:bg-white/5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 hover:text-slate-800 dark:hover:text-white transition-colors group"
                  title="Admin Access"
                >
                    <Lock size={14} className="group-hover:scale-110 transition-transform" />
                </button>
                <span className="opacity-20 mx-2">|</span>
                <span>© 2026 IQODEX</span>
            </div>
         </div>
      </div>
    </div>
  );
};